"use client";

import { motion } from "framer-motion";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/input/DropdownMenu";
import { useWorkbookContext } from "@/contexts/WorkbookContext";
import { useAuth } from "@/contexts/AuthContext";
import { workbooksApi } from "@/lib/api/apiwrapper";
import Link from "next/link";
import LogoutButton from "@/components/auth/LogoutButton";
import { Notebook, Plus, ShieldCheck, Check } from "lucide-react";
import { getHumanReadableRole } from "@/utils/roleUtils";
import { usePathname } from "@/i18n/navigation";
import { cn } from "@/lib/utils";
import LocaleSwitcher from "./LocaleSwitcher";
import { useTranslations } from "next-intl";
import { useHydrated } from "@/hooks/useHydrated";
import { HorizontalContainer } from "@/components/layout/containers/HorizontalContainer";
import { Button } from "@/components/ui/Button";
import { ExploreNavbarSearch } from "./ExploreNavbarSearch";

export function ExploreNavbar() {
  const isHydrated = useHydrated();
  const { current, myWorkbooks, select, clear } = useWorkbookContext();
  const pathname = usePathname();
  const isMap = pathname.split("/")[1] == "map";

  const springTransition: import("framer-motion").Transition = { type: "spring", bounce: 0.15, duration: 0.5 };

  return (
    <motion.div
      style={{ borderRadius: isMap ? 16 : 0 }}
      layout
      transition={springTransition}
      className={isMap ? "absolute inset-x-0 top-4 z-50 mx-auto w-[92%] max-w-[820px] border bg-background/90 shadow-lg backdrop-blur-xl" : "w-full border-b bg-background/80 backdrop-blur-md"}
    >
      <motion.div
        layout
        transition={springTransition}
        className={isMap ? "px-3 py-2" : "mx-auto max-w-screen-xl px-4 py-3 sm:px-6 lg:px-8"}
      >
        <motion.div
          layout
          transition={springTransition}
          className="flex items-center gap-2"
        >
          {/* Search — always present, flexes to fill */}
          <div className="min-w-0 flex-1">
            <ExploreNavbarSearch />
          </div>

          {/* ─── Controls (always compact) ────────────────── */}
          {isHydrated ? <HorizontalContainer className="shrink-0 gap-1.5">
            <LocaleSwitcher />
            <RoleDropdown />
            <WorkbookDropdown
              current={current}
              myWorkbooks={myWorkbooks}
              select={select}
              clear={clear}
              isHydrated={isHydrated}
            />

            <LogoutButton variant="icon" />
          </HorizontalContainer> : <div className="flex shrink-0 items-center gap-1.5" />}
        </motion.div>

        {/* Row 2: Spend toolbar (only on explore pages)
        <AnimatePresence>
          {!isMap && pathname?.includes("/explore") && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <SpendToolbar />
            </motion.div>
          )}
        </AnimatePresence> */}
      </motion.div>
    </motion.div>
  );
}

// ─── Workbook Dropdown ──────────────────────────────────────

function WorkbookDropdown({ current, myWorkbooks, select, clear, isHydrated }: { current: any; myWorkbooks: any[]; select: (wb: any) => void; clear: () => void; isHydrated: boolean; }) {
  const t = useTranslations();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          title={isHydrated ? (current?.title ?? "No workbook") : "No workbook"}
          className="relative shadow-none h-9 w-9 rounded-lg border bg-background text-muted-foreground hover:bg-accent"
        >
          <Notebook className="h-4 w-4" />
          {isHydrated && current && <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-primary" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-64"
      >
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Workbooks</span>
          <Button
            type="button"
            size="sm"
            className="rounded bg-muted px-2 py-1 shadow-none text-foreground hover:bg-accent h-6 gap-0 [&_svg]:size-3"
            onClick={async (e) => {
              e.preventDefault();
              const title = typeof window !== "undefined" ? window.prompt("Workbook title", "My workbook") : "My workbook";
              if (!title) return;
              try {
                const wb = await workbooksApi.workbooksCreatePOST({
                  createWorkbookRequestDto: {
                    title,
                    isPublic: true,
                    requireLogin: false,
                  },
                });
                select({
                  id: wb.id!,
                  slug: wb.slug!,
                  title: wb.title!,
                });
              } catch (err) {
                console.error(err);
                if (typeof window !== "undefined") window.alert("Failed to create workbook");
              }
            }}
          >
            <Plus className="mr-1 h-3 w-3" /> New
          </Button>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {current ? (
          <DropdownMenuItem
            className="gap-0"
            onClick={(e) => {
              e.preventDefault();
              clear();
            }}
          >
            {t("workbooks.clear")} &quot;
            <span className="m-0 font-semibold">{current.title}</span>
            &quot;
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem disabled>No current workbook</DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        {myWorkbooks.filter((wb) => wb.slug !== current?.slug).length === 0 ? (
          <DropdownMenuItem disabled>No recent workbooks</DropdownMenuItem>
        ) : (
          myWorkbooks
            .filter((wb) => wb.slug !== current?.slug)
            .map((wb) => (
              <div
                key={wb.slug}
                className="flex items-center justify-between px-2 py-1.5"
              >
                <Button
                  variant="ghost"
                  type="button"
                  className="text-left p-0 gap-0 h-5 font-normal text-sm hover:underline"
                  onClick={() => select(wb)}
                >
                  {wb.title}
                </Button>
              </div>
            ))
        )}
        <DropdownMenuSeparator />
        {current && (
          <div className="px-2 py-1.5 text-xs text-muted-foreground">
            <Link
              className="hover:underline"
              href={`/workbooks/${current.slug}`}
            >
              Open preview
            </Link>
            <span className="mx-1">·</span>
            <Link
              className="hover:underline"
              href={`/workbooks/${current.slug}/editor`}
            >
              Open editor
            </Link>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ─── Role Dropdown ──────────────────────────────────────────

function RoleDropdown() {
  const { user, switchRole } = useAuth();
  const t = useTranslations();

  const handleSwitchRole = async (newRoleId: string | undefined) => {
    if (!newRoleId || !user?.roles.find((r) => r.id == newRoleId)?.id) return;
    await switchRole(newRoleId);
  };

  const currentRole = user?.currentRole;

  if (!user?.roles || user.roles.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          title={currentRole?.roleName ? getHumanReadableRole(currentRole.roleName) : "Select role"}
          className="relative shadow-none h-9 w-9 rounded-lg border bg-background text-muted-foreground hover:bg-accent"
        >
          <ShieldCheck className="h-4 w-4" />
          {currentRole && <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-primary" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-64"
      >
        <DropdownMenuLabel>{t("roles.switchRole")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {user.roles.map((r) => {
          const isActive = r.id === currentRole?.id;
          return (
            <DropdownMenuItem
              key={r.id}
              disabled={isActive}
              className={cn("flex cursor-pointer items-center justify-between", isActive && "cursor-not-allowed opacity-60")}
              onClick={() => handleSwitchRole(r.id)}
            >
              <span>
                {getHumanReadableRole(r.roleName)}
                {r.organizationName ? <span className="ml-1 text-xs text-muted-foreground">({r.organizationName})</span> : null}
              </span>
              {isActive && <Check className="h-4 w-4 text-primary" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
